import type { BrandRequestListItem } from "@nlc/shared-types";
import type { SortDescriptor } from "react-stately";

import { AlertTriangle } from "lucide-react";
import { useMemo, useState } from "react";

import { LazyConfigurableTable } from "@/features/requests/components/tables/LazyConfigurableTable";
import { PAGINATION_DEFAULTS } from "@/features/requests/constants/pagination";
import { useIsMobile } from "@/hooks/use-mobile";

import type { BrandRequestFilterId } from "./config/filterConfig";

import { getTableConfigForFilter } from "./config/tableConfigs";
import { useBrandRequests } from "./hooks/useBrandRequests";

interface BrandRequestsTableProps {
  brandId: number;
  debouncedSearch: string;
  filter: BrandRequestFilterId;
}

export function BrandRequestsTable({
  brandId,
  debouncedSearch,
  filter,
}: BrandRequestsTableProps) {
  const [currentPage, setCurrentPage] = useState<number>(
    PAGINATION_DEFAULTS.DEFAULT_PAGE,
  );
  const [itemsPerPage, setItemsPerPage] = useState<number>(
    PAGINATION_DEFAULTS.DEFAULT_ITEMS_PER_PAGE,
  );
  const [sortDescriptor, setSortDescriptor] = useState<
    SortDescriptor | undefined
  >();

  const isMobile = useIsMobile();

  const tableConfig = useMemo(() => {
    const config = getTableConfigForFilter(filter);

    if (!isMobile) return config;

    return {
      ...config,
      columns: config.columns.map((col) =>
        col.mobileWidth ? { ...col, width: col.mobileWidth } : col,
      ),
    };
  }, [filter, isMobile]);

  const { data, isError, isLoading } = useBrandRequests({
    brandId,
    limit: itemsPerPage,
    page: currentPage,
    search: debouncedSearch || undefined,
    sortDescriptor,
    tab: filter,
  });

  const requests = data?.requests ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.pagination?.totalPages ?? 0;

  const paginationProps = useMemo(
    () => ({
      metadata: {
        currentPage,
        itemsPerPage,
        totalItems: total,
        totalPages,
      },
      onItemsPerPageChange: (newSize: number) => {
        setItemsPerPage(newSize);
        setCurrentPage(1);
      },
      onPageChange: setCurrentPage,
      pageSizeOptions:
        PAGINATION_DEFAULTS.PAGE_SIZE_OPTIONS as unknown as number[],
      showPageSizeSelector: true,
    }),
    [currentPage, itemsPerPage, total, totalPages],
  );

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-md py-6xl">
        <AlertTriangle className="size-8 text-text-error-primary" />
        <span className="text-sm font-medium text-text-primary">
          Failed to load requests
        </span>
        <span className="text-sm text-text-tertiary">
          Please try again later.
        </span>
      </div>
    );
  }

  return (
    <LazyConfigurableTable<BrandRequestListItem>
      config={tableConfig}
      count={total}
      data={requests}
      isLoading={isLoading}
      paginationProps={paginationProps}
      setSortDescriptor={setSortDescriptor}
      sortDescriptor={sortDescriptor}
      useAdvancedSorting
    />
  );
}
